import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  DestroyRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CollapsibleSectionComponent } from '../../../shared/collapsible-section/collapsible-section.component';
import {
  CreateModalLandingPageComponent,
  LandingPageModalData,
  DEFAULT_LANDING_PAGE_DATA,
} from './create-modal-landing-page/create-modal-landing-page.component';
import {
  CreateModalSuccessPageComponent,
  SuccessPageModalData,
  DEFAULT_SUCCESS_PAGE_DATA,
} from './create-modal-success-page/create-modal-success-page.component';
import { CreateSmsEmailModalComponent } from './create-sms-email-modal/create-sms-email-modal.component';
import {
  findCommonWebsiteContent,
  hasLandingPageApiContent,
  hasSuccessPageApiContent,
  isOptionalSettingsLandingComplete,
  mapCampaignsToPreviousLandingPageOptions,
  mapCampaignsToPreviousSuccessPageOptions,
  mapLandingApiToModal,
  mapLandingModalToApi,
  mapSuccessApiToModal,
  PreviousLandingPageOption,
  PreviousSuccessPageOption,
  shouldShowSuccessCta,
  withCommonWebsiteLandingPage,
} from './common-website-content.mapper';
import {
  resolveLandingPageViewUrl,
  buildSmsCampaignSuccessPageUrl,
  buildCampaignLandingPageUrl,
} from './campaign-platform-url.util';
import { buildConsentTextSegments, ConsentTextSegment } from './consent-link.util';
import { replaceSuccessPageMergeFields } from './success-page-merge-fields.util';
import { buildResendPreviewStatusText } from './success-page-resend.util';
import { CampaignService } from '../../../services/campaign.service';
import {
  DEFAULT_SMS_EMAIL_DATA,
  EMPTY_MMS_RICH_CARD,
  EMPTY_RCS_RICH_CARD,
  DeliveryContentApi,
  applyRichCardFields,
  clearRichCardFieldsFromForm,
  clearSmsFieldsFromForm,
  findDeliveryEmailContent,
  findMmsContent,
  findRcsContent,
  findSmsDeliveryContent,
  hasEmailDeliveryApiContent,
  hasMmsDeliveryApiContent,
  hasRcsDeliveryApiContent,
  hasSmsDeliveryApiContent,
  mapDeliveryContentsToModal,
  mapDeliveryEmailModalToApi,
  mapDeliveryEmailToPreviewData,
  mapMmsApiToModal,
  mapMmsModalToApi,
  mapRcsApiToModal,
  mapRcsModalToApi,
  mapSmsDeliveryApiToModal,
  mapSmsDeliveryModalToApi,
  normalizeChannelFlags,
  pickRichCardFields,
  RichCardFormData,
  SmsEmailModalData,
  DeliveryPreviewTabId,
} from './sms-delivery.mapper';

export type { DeliveryPreviewTabId };
import { NotificationService } from '../../../services/notification.service'; // NotificationService;

export type OptionalSettingSection = 'landing' | 'success' | 'delivery';

export type { PreviousLandingPageOption, PreviousSuccessPageOption };

@Component({
  selector: 'app-optional-setting',
  imports: [
    CommonModule,
    FormsModule,
    CollapsibleSectionComponent,
    CreateModalLandingPageComponent,
    CreateModalSuccessPageComponent,
    CreateSmsEmailModalComponent,
  ],
  templateUrl: './optional-setting.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './optional-setting.component.scss',
})
export class OptionalSettingComponent implements OnInit, OnChanges {
  @Input() campaignData: any = {};
  @Input() currentCampaignId: string | null = null;
  @Input() isCompleted = false;
  @Input() activeSection: OptionalSettingSection | '' = 'landing';
  /** Optional override; normally populated from GET /campaign. */
  @Input() previousLandingPages: PreviousLandingPageOption[] = [];
  @Output() done = new EventEmitter<void>();
  @Output() landingPageChange = new EventEmitter<
    LandingPageModalData & { section: 'landing_page' }
  >();
  @Output() landingPageClear = new EventEmitter<void>();
  @Output() successPageChange = new EventEmitter<
    SuccessPageModalData & { section: 'success_page' }
  >();
  @Output() successPageClear = new EventEmitter<void>();
  @Output() deliveryChange = new EventEmitter<DeliveryContentApi>();
  @Output() deliveryBatchChange = new EventEmitter<DeliveryContentApi[]>();
  @Output() deliveryEmailClear = new EventEmitter<void>();
  @Output() deliveryClear = new EventEmitter<void>();
  @Output() navigateToSection = new EventEmitter<{ tabId: string; section?: string }>();

  landingPageExpanded = true;
  successPageExpanded = false;
  smsEmailDeliveryExpanded = false;

  showLandingPageModal = false;
  showSuccessPageModal = false;
  showSmsEmailModal = false;
  landingPageData: LandingPageModalData | null = null;
  successPageData: SuccessPageModalData | null = null;
  deliveryData: SmsEmailModalData | null = null;
  isLandingPageEditMode = false;
  isSuccessPageEditMode = false;
  isDeliveryEditMode = false;
  selectedDeliveryPreviewTab: DeliveryPreviewTabId = 'sms';
  landingPageCreateExpanded = false;
  selectedPreviousLandingPageId = '';
  loadedPreviousLandingPages: PreviousLandingPageOption[] = [];
  isLoadingPreviousLandingPages = false;
  successPageCreateExpanded = false;
  selectedPreviousSuccessPageId = '';
  loadedPreviousSuccessPages: PreviousSuccessPageOption[] = [];
  isLoadingPreviousSuccessPages = false;
  isPreviousLandingPageDropdownOpen = false;
  isPreviousSuccessPageDropdownOpen = false;

  private messageService = inject(NotificationService);
  private campaignService = inject(CampaignService);
  private destroyRef = inject(DestroyRef);
  private reuseCampaignsLoadStarted = false;

  ngOnInit(): void {
    this.applyActiveSection(this.activeSection || 'landing');
    this.syncFromCampaignData();
    this.loadReuseCampaignOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeSection']) {
      this.applyActiveSection(this.activeSection || 'landing');
    }
    if (changes['campaignData']) {
      this.syncFromCampaignData();
      if (!this.showSmsEmailDeliverySection) {
        this.smsEmailDeliveryExpanded = false;
      } else {
        this.syncDeliveryPreviewTab();
      }
    }
    if (changes['currentCampaignId'] && !changes['currentCampaignId'].firstChange) {
      this.reloadReuseCampaignOptions();
    }
  }

  get hasLandingPageContent(): boolean {
    if (this.landingPageData) {
      return Boolean(this.landingPageData.title?.trim() || this.landingPageData.buttonText?.trim());
    }
    const commonWebsite = findCommonWebsiteContent(this.campaignData?.contents);
    return hasLandingPageApiContent(commonWebsite?.landing_page);
  }

  /** Show delivery section when campaign platforms include SMS and/or email. */
  get showSmsEmailDeliverySection(): boolean {
    return this.smsDeliveryPlatformEnabled || this.emailDeliveryPlatformEnabled;
  }

  /** Hidden until the landing page is saved (CWS-1148); the gate reads the saved contents. */
  get landingPageUrl(): string | null {
    if (!isOptionalSettingsLandingComplete(this.campaignData?.contents)) {
      return null;
    }
    const platforms = this.campaignData?.platform ?? this.campaignData?.platforms;
    return buildCampaignLandingPageUrl(this.currentCampaignId, platforms);
  }

  get smsDeliveryPlatformEnabled(): boolean {
    return this.isCampaignPlatformSelected('sms');
  }

  get emailDeliveryPlatformEnabled(): boolean {
    return this.isCampaignPlatformSelected('email');
  }

  get deliveryPreviewTabs(): { id: DeliveryPreviewTabId; label: string }[] {
    const tabs: { id: DeliveryPreviewTabId; label: string }[] = [];
    if (this.smsDeliveryPlatformEnabled) {
      tabs.push(
        { id: 'sms', label: 'SMS' },
        { id: 'mms', label: 'MMS' }
        // { id: 'rcs', label: 'RCS' }
      );
    }
    if (this.emailDeliveryPlatformEnabled) {
      tabs.push({ id: 'email', label: 'Email' });
    }
    return tabs;
  }

  get hasSuccessPageContent(): boolean {
    if (this.successPageData) {
      return Boolean(this.successPageData.title?.trim() || this.successPageData.body?.trim());
    }
    const commonWebsite = findCommonWebsiteContent(this.campaignData?.contents);
    return hasSuccessPageApiContent(commonWebsite?.success_page);
  }

  onLandingPageToggle(expanded: boolean): void {
    if (expanded) {
      this.successPageExpanded = false;
      this.smsEmailDeliveryExpanded = false;
    }
    this.landingPageExpanded = expanded;
  }

  onSuccessPageToggle(expanded: boolean): void {
    if (expanded) {
      this.landingPageExpanded = false;
      this.smsEmailDeliveryExpanded = false;
    }
    this.successPageExpanded = expanded;
  }

  onSmsEmailDeliveryToggle(expanded: boolean): void {
    if (expanded) {
      this.landingPageExpanded = false;
      this.successPageExpanded = false;
    }
    this.smsEmailDeliveryExpanded = expanded;
  }

  onLandingPageAddClick(): void {
    this.landingPageCreateExpanded = !this.landingPageCreateExpanded;
    if (!this.landingPageCreateExpanded) {
      this.selectedPreviousLandingPageId = '';
      return;
    }
    this.loadReuseCampaignOptions();
  }

  onCreateLandingPage(): void {
    this.isLandingPageEditMode = this.hasLandingPageContent;
    this.showLandingPageModal = true;
  }

  onLandingPageCreateClick(): void {
    const selectedId = this.selectedPreviousLandingPageId?.trim();
    if (!selectedId) {
      this.onCreateLandingPage();
      return;
    }

    const selected = this.previousLandingPageOptions.find(option => option.id === selectedId);
    if (selected) {
      this.landingPageData = { ...selected.data };
      this.landingPageChange.emit({ ...selected.data, section: 'landing_page' });
      this.landingPageCreateExpanded = false;
      this.selectedPreviousLandingPageId = '';
      this.messageService.add({
        severity: 'success',
        summary: 'Landing page added!',
        detail: 'The previous landing page has been applied to this campaign.',
      });
      return;
    }
    this.onCreateLandingPage();
  }

  get previousLandingPageOptions(): PreviousLandingPageOption[] {
    if (this.loadedPreviousLandingPages.length) {
      return this.loadedPreviousLandingPages;
    }
    return this.previousLandingPages ?? [];
  }

  getPreviousLandingPageDisplayText(): string {
    if (!this.selectedPreviousLandingPageId?.trim()) {
      return 'Select a Landing Page';
    }
    return (
      this.previousLandingPageOptions.find(
        option => option.id === this.selectedPreviousLandingPageId
      )?.label ?? 'Select a Landing Page'
    );
  }

  getPreviousSuccessPageDisplayText(): string {
    if (!this.selectedPreviousSuccessPageId?.trim()) {
      return 'Select a Success Page';
    }
    return (
      this.previousSuccessPageOptions.find(
        option => option.id === this.selectedPreviousSuccessPageId
      )?.label ?? 'Select a Success Page'
    );
  }

  togglePreviousLandingPageDropdown(event: Event): void {
    event.stopPropagation();
    if (this.isLoadingPreviousLandingPages) {
      return;
    }
    this.isPreviousLandingPageDropdownOpen = !this.isPreviousLandingPageDropdownOpen;
    if (this.isPreviousLandingPageDropdownOpen) {
      this.isPreviousSuccessPageDropdownOpen = false;
    }
  }

  togglePreviousSuccessPageDropdown(event: Event): void {
    event.stopPropagation();
    if (this.isLoadingPreviousSuccessPages) {
      return;
    }
    this.isPreviousSuccessPageDropdownOpen = !this.isPreviousSuccessPageDropdownOpen;
    if (this.isPreviousSuccessPageDropdownOpen) {
      this.isPreviousLandingPageDropdownOpen = false;
    }
  }

  selectPreviousLandingPage(optionId: string, event: Event): void {
    event.stopPropagation();
    this.selectedPreviousLandingPageId = optionId;
    this.isPreviousLandingPageDropdownOpen = false;
  }

  selectPreviousSuccessPage(optionId: string, event: Event): void {
    event.stopPropagation();
    this.selectedPreviousSuccessPageId = optionId;
    this.isPreviousSuccessPageDropdownOpen = false;
  }

  @HostListener('document:click')
  closeReusePageDropdowns(): void {
    this.isPreviousLandingPageDropdownOpen = false;
    this.isPreviousSuccessPageDropdownOpen = false;
  }

  onViewLandingPage(): void {
    const platforms = this.campaignData?.platform ?? this.campaignData?.platforms;
    const url = resolveLandingPageViewUrl(platforms, this.currentCampaignId);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Landing page unavailable',
        detail: 'Landing page URL is not available yet. Save the campaign and check UTM tagging.',
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  copyLandingPageUrl(): void {
    const url = this.landingPageUrl?.trim();
    if (!url) {
      return;
    }
    navigator.clipboard.writeText(url).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Landing page URL copied to clipboard.',
        life: 2000,
      });
    });
  }

  onNavigateToUtmTagging(): void {
    this.navigateToSection.emit({ tabId: 'overview', section: 'UTM tagging' });
  }

  onSuccessPageAddClick(): void {
    this.successPageCreateExpanded = !this.successPageCreateExpanded;
    if (!this.successPageCreateExpanded) {
      this.selectedPreviousSuccessPageId = '';
      return;
    }
    this.loadReuseCampaignOptions();
  }

  onCreateSuccessPage(): void {
    this.isSuccessPageEditMode = this.hasSuccessPageContent;
    this.showSuccessPageModal = true;
  }

  onSuccessPageCreateClick(): void {
    const selectedId = this.selectedPreviousSuccessPageId?.trim();
    if (!selectedId) {
      this.onCreateSuccessPage();
      return;
    }

    const selected = this.previousSuccessPageOptions.find(option => option.id === selectedId);
    if (selected) {
      this.successPageData = { ...selected.data };
      this.successPageChange.emit({ ...selected.data, section: 'success_page' });
      this.successPageCreateExpanded = false;
      this.selectedPreviousSuccessPageId = '';
      this.messageService.add({
        severity: 'success',
        summary: 'Success page added!',
        detail: 'The previous success page has been applied to this campaign.',
      });
      return;
    }
    this.onCreateSuccessPage();
  }

  get previousSuccessPageOptions(): PreviousSuccessPageOption[] {
    return this.loadedPreviousSuccessPages;
  }

  onViewSuccessPage(): void {
    const url = buildSmsCampaignSuccessPageUrl(this.currentCampaignId);
    if (!url) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Success page unavailable',
        detail: 'Save the campaign first to preview the success page.',
      });
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  get hasDeliveryContent(): boolean {
    const contents = this.campaignData?.contents;
    const hasSmsFromApi =
      this.smsDeliveryPlatformEnabled &&
      (hasRcsDeliveryApiContent(findRcsContent(contents)) ||
        hasMmsDeliveryApiContent(findMmsContent(contents)) ||
        hasSmsDeliveryApiContent(findSmsDeliveryContent(contents)));
    const hasEmailFromApi =
      this.emailDeliveryPlatformEnabled &&
      hasEmailDeliveryApiContent(findDeliveryEmailContent(contents));
    if (!this.deliveryData) {
      return hasSmsFromApi || hasEmailFromApi;
    }
    const hasLocalMms =
      this.smsDeliveryPlatformEnabled &&
      this.deliveryData.messageType === 'mms' &&
      this.hasMeaningfulRichCard(this.deliveryData.mmsRichCard);
    const hasLocalRcs =
      this.smsDeliveryPlatformEnabled &&
      this.deliveryData.messageType === 'rcs' &&
      this.hasMeaningfulRichCard(this.deliveryData.rcsRichCard);
    const hasLocalSms =
      this.smsDeliveryPlatformEnabled &&
      Boolean(this.deliveryData.messageBody?.trim() || this.deliveryData.senderContact?.trim());
    const hasLocalEmail =
      this.emailDeliveryPlatformEnabled &&
      this.deliveryData.emailChannelEnabled &&
      Boolean(
        this.deliveryData.emailSubject?.trim() ||
        this.deliveryData.emailPreheader?.trim() ||
        this.deliveryData.emailBody?.trim()
      );
    return (
      hasSmsFromApi || hasEmailFromApi || hasLocalMms || hasLocalRcs || hasLocalSms || hasLocalEmail
    );
  }

  onCreateDelivery(): void {
    this.isDeliveryEditMode = this.hasDeliveryContent;
    if (!this.deliveryPreviewTabs.some(tab => tab.id === this.selectedDeliveryPreviewTab)) {
      this.selectedDeliveryPreviewTab = this.getDefaultDeliveryPreviewTab();
    }
    this.showSmsEmailModal = true;
  }

  onEditDelivery(): void {
    this.onCreateDelivery();
  }

  onLandingPageAction(): void {
    if (!this.hasLandingPageContent) {
      if (!this.selectedPreviousLandingPageId?.trim()) {
        this.onCreateLandingPage();
        return;
      }
      if (!this.landingPageCreateExpanded) {
        this.landingPageCreateExpanded = true;
        this.loadReuseCampaignOptions();
        return;
      }
      this.onLandingPageCreateClick();
      return;
    }
    this.advanceToSuccessPageSection();
  }

  onSuccessPageAction(): void {
    if (!this.hasSuccessPageContent) {
      if (!this.selectedPreviousSuccessPageId?.trim()) {
        this.onCreateSuccessPage();
        return;
      }
      if (!this.successPageCreateExpanded) {
        this.successPageCreateExpanded = true;
        this.loadReuseCampaignOptions();
        return;
      }
      this.onSuccessPageCreateClick();
      return;
    }
    this.advanceToDeliverySection();
  }

  onDeliveryAction(): void {
    if (!this.hasDeliveryContent) {
      this.onCreateDelivery();
      return;
    }
    this.completeOptionalSettingsWorkflow();
  }

  onCloseLandingPageModal(): void {
    this.showLandingPageModal = false;
    this.isLandingPageEditMode = false;
  }

  onSaveLandingPageModal(data: LandingPageModalData): void {
    this.landingPageData = { ...data };
    this.showLandingPageModal = false;
    this.patchLandingPageContents(data);

    if (!this.isLandingPageEditMode) {
      this.messageService.add({
        severity: 'success',
        summary: 'Landing page created!',
        detail: 'The landing page has been created successfully.',
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Landing page updated!',
        detail: 'The landing page has been updated successfully.',
      });
    }

    this.landingPageChange.emit({ ...data, section: 'landing_page' });
    this.isLandingPageEditMode = false;
    this.advanceToSuccessPageSection();
  }

  /**
   * Mirrors the saved landing page into `campaignData.contents` (same shared object the editor
   * mutates) so the readiness-gated landing URL shows right away, without a reload.
   */
  private patchLandingPageContents(data: LandingPageModalData): void {
    if (!this.campaignData) {
      return;
    }
    this.campaignData.contents = withCommonWebsiteLandingPage(
      this.campaignData.contents,
      mapLandingModalToApi(data)
    );
  }

  onClearLandingPage(): void {
    this.landingPageData = null;
    this.landingPageCreateExpanded = false;
    this.selectedPreviousLandingPageId = '';
    this.landingPageClear.emit();
  }

  onCloseSuccessPageModal(): void {
    this.showSuccessPageModal = false;
    this.isSuccessPageEditMode = false;
  }

  onSaveSuccessPageModal(data: SuccessPageModalData): void {
    this.successPageData = { ...data };
    this.showSuccessPageModal = false;

    if (!this.isSuccessPageEditMode) {
      this.messageService.add({
        severity: 'success',
        summary: 'Success page created!',
        detail: 'The success page has been created successfully.',
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'Success page updated!',
        detail: 'The success page has been updated successfully.',
      });
    }

    this.successPageChange.emit({ ...data, section: 'success_page' });
    this.isSuccessPageEditMode = false;
    this.advanceToDeliverySection();
  }

  onClearSuccessPage(): void {
    this.successPageData = null;
    this.successPageCreateExpanded = false;
    this.selectedPreviousSuccessPageId = '';
    this.successPageClear.emit();
  }

  onCloseSmsEmailModal(): void {
    this.showSmsEmailModal = false;
    this.isDeliveryEditMode = false;
  }

  onSaveSmsEmailModal(data: SmsEmailModalData): void {
    const previous = this.deliveryData ?? mapDeliveryContentsToModal(this.campaignData?.contents);
    const mmsRichCard = {
      ...(previous.mmsRichCard ?? EMPTY_MMS_RICH_CARD),
      ...(data.messageType === 'mms' ? (data.mmsRichCard ?? pickRichCardFields(data)) : {}),
    };
    const rcsRichCard = {
      ...(previous.rcsRichCard ?? EMPTY_RCS_RICH_CARD),
      ...(data.messageType === 'rcs' ? (data.rcsRichCard ?? pickRichCardFields(data)) : {}),
    };
    const channelFlags = normalizeChannelFlags(data);
    let deliveryData: SmsEmailModalData = {
      ...previous,
      ...channelFlags,
      emailSubject: data.emailChannelEnabled ? data.emailSubject : previous.emailSubject,
      emailPreheader: data.emailChannelEnabled ? data.emailPreheader : previous.emailPreheader,
      emailBody: data.emailChannelEnabled ? data.emailBody : previous.emailBody,
      senderContact: data.smsChannelEnabled ? data.senderContact : previous.senderContact,
      smsLink: data.smsChannelEnabled ? data.smsLink : previous.smsLink,
      messageBody: data.smsChannelEnabled ? data.messageBody : previous.messageBody,
      messageType: data.messageType,
      mmsRichCard,
      rcsRichCard,
    };

    if (data.messageType === 'mms') {
      deliveryData = applyRichCardFields({ ...deliveryData, messageType: 'mms' }, mmsRichCard);
    } else if (data.messageType === 'rcs') {
      deliveryData = applyRichCardFields({ ...deliveryData, messageType: 'rcs' }, rcsRichCard);
    } else if (data.smsChannelEnabled) {
      deliveryData = {
        ...clearRichCardFieldsFromForm(deliveryData),
        messageType: 'sms',
        messageBody: data.messageBody,
        mmsRichCard,
        rcsRichCard,
      };
    } else {
      deliveryData = {
        ...clearSmsFieldsFromForm(deliveryData),
        emailSubject: data.emailSubject,
        emailPreheader: data.emailPreheader,
        emailBody: data.emailBody,
        mmsRichCard,
        rcsRichCard,
      };
    }

    if (!data.emailChannelEnabled) {
      deliveryData = {
        ...deliveryData,
        emailSubject: previous.emailSubject,
        emailPreheader: previous.emailPreheader,
        emailBody: previous.emailBody,
      };
    }

    this.deliveryData = deliveryData;
    this.showSmsEmailModal = false;

    if (!this.isDeliveryEditMode) {
      this.messageService.add({
        severity: 'success',
        summary: 'SMS/Email delivery configured!',
        detail: 'Delivery settings have been saved successfully.',
      });
    } else {
      this.messageService.add({
        severity: 'success',
        summary: 'SMS/Email delivery updated!',
        detail: 'Delivery settings have been updated successfully.',
      });
    }

    const deliveryPayloads = this.buildDeliverySavePayloads(data);
    if (deliveryPayloads.length > 0) {
      this.deliveryBatchChange.emit(deliveryPayloads);
    }
    if (
      !data.emailChannelEnabled &&
      hasEmailDeliveryApiContent(findDeliveryEmailContent(this.campaignData?.contents))
    ) {
      this.deliveryEmailClear.emit();
    }

    if (data.emailChannelEnabled && this.emailDeliveryPlatformEnabled) {
      this.selectedDeliveryPreviewTab = 'email';
    } else if (data.smsChannelEnabled && this.smsDeliveryPlatformEnabled) {
      if (data.messageType === 'mms') {
        this.selectedDeliveryPreviewTab = 'mms';
      } else if (data.messageType === 'rcs') {
        this.selectedDeliveryPreviewTab = 'rcs';
      } else {
        this.selectedDeliveryPreviewTab = 'sms';
      }
    } else {
      this.syncDeliveryPreviewTab();
    }
    this.isDeliveryEditMode = false;
    this.smsEmailDeliveryExpanded = false;
  }

  onClearDelivery(): void {
    this.deliveryData = null;
    this.selectedDeliveryPreviewTab = this.getDefaultDeliveryPreviewTab();
    this.deliveryClear.emit();
  }

  selectDeliveryPreviewTab(tabId: DeliveryPreviewTabId): void {
    this.selectedDeliveryPreviewTab = tabId;
  }

  isDeliveryTabActive(tabId: DeliveryPreviewTabId): boolean {
    return this.selectedDeliveryPreviewTab === tabId;
  }

  get hasSmsTabContent(): boolean {
    return this.getSmsTabPreviewData() !== null;
  }

  get hasMmsTabContent(): boolean {
    return this.getMmsTabPreviewData() !== null;
  }

  get hasRcsTabContent(): boolean {
    return this.getRcsTabPreviewData() !== null;
  }

  get hasEmailTabContent(): boolean {
    return this.getEmailTabPreviewData() !== null;
  }

  getEmailTabPreviewSubject(): string {
    const preview = this.getEmailTabPreviewData();
    const text = preview?.emailSubject?.trim();
    return text ? this.formatDeliveryPreviewText(text) : '';
  }

  getEmailTabPreviewPreheader(): string {
    const preview = this.getEmailTabPreviewData();
    const text = preview?.emailPreheader?.trim();
    return text ? this.formatDeliveryPreviewText(text) : '';
  }

  getEmailTabPreviewBody(): string {
    const preview = this.getEmailTabPreviewData();
    const raw = preview?.emailBody?.trim();
    if (!raw) {
      return '';
    }
    return this.formatDeliveryPreviewText(this.stripHtmlForEmailPreview(raw));
  }

  get mmsTabPreviewCouponUrl(): string {
    return this.getMmsTabPreviewData()?.couponImageUrl || '';
  }

  get mmsTabPreviewCaption(): string {
    return this.getMmsTabPreviewData()?.imageCaption?.trim() || '';
  }

  get mmsTabPreviewAltText(): string {
    return this.getMmsTabPreviewData()?.imageAltText?.trim() || 'Coupon image';
  }

  get rcsTabPreviewCouponUrl(): string {
    return this.getRcsTabPreviewData()?.couponImageUrl || '';
  }

  get rcsTabPreviewCaption(): string {
    return this.getRcsTabPreviewData()?.imageCaption?.trim() || '';
  }

  get rcsTabPreviewAltText(): string {
    return this.getRcsTabPreviewData()?.imageAltText?.trim() || 'Coupon image';
  }

  getSmsTabPreviewMessage(): string {
    const preview = this.getSmsTabPreviewData();
    const text = preview?.messageBody?.trim();
    if (!text) {
      return '';
    }
    return this.formatDeliveryPreviewText(text);
  }

  getMmsTabPreviewMessage(): string {
    const preview = this.getMmsTabPreviewData();
    const text = preview?.bubble2TextBody?.trim() || preview?.messageBody?.trim();
    if (!text) {
      return '';
    }
    return this.formatDeliveryPreviewText(text);
  }

  getRcsTabPreviewMessage(): string {
    const preview = this.getRcsTabPreviewData();
    const text = preview?.bubble2TextBody?.trim() || preview?.messageBody?.trim();
    if (!text) {
      return '';
    }
    return this.formatDeliveryPreviewText(text);
  }

  private buildDeliverySavePayloads(data: SmsEmailModalData): DeliveryContentApi[] {
    const payloads: DeliveryContentApi[] = [];
    if (data.emailChannelEnabled) {
      payloads.push(mapDeliveryEmailModalToApi(data));
    }
    if (data.smsChannelEnabled) {
      if (data.messageType === 'mms') {
        payloads.push(mapMmsModalToApi(data));
      } else if (data.messageType === 'rcs') {
        payloads.push(mapRcsModalToApi(data));
      } else {
        payloads.push(mapSmsDeliveryModalToApi(data));
      }
    }
    return payloads;
  }

  private getSmsTabPreviewData(): SmsEmailModalData | null {
    const contents = this.campaignData?.contents;
    const smsApi = findSmsDeliveryContent(contents);
    const local = this.deliveryData;
    const localHasSms =
      local?.smsChannelEnabled !== false &&
      (local?.messageBody?.trim() || local?.senderContact?.trim()) &&
      local?.messageType === 'sms';

    if (localHasSms) {
      const fromApi =
        smsApi && hasSmsDeliveryApiContent(smsApi) ? mapSmsDeliveryApiToModal(smsApi) : null;
      return {
        ...(fromApi ?? { ...DEFAULT_SMS_EMAIL_DATA, messageType: 'sms' as const }),
        ...local,
        messageType: 'sms',
        messageBody: local!.messageBody || '',
        senderContact: local!.senderContact || '',
        smsLink: local!.smsLink || '',
      };
    }

    if (smsApi && hasSmsDeliveryApiContent(smsApi)) {
      return mapSmsDeliveryApiToModal(smsApi);
    }
    return null;
  }

  private getMmsTabPreviewData(): SmsEmailModalData | null {
    const contents = this.campaignData?.contents;
    const mms = findMmsContent(contents);
    if (mms && hasMmsDeliveryApiContent(mms)) {
      return mapMmsApiToModal(mms);
    }
    const local = this.deliveryData;
    if (!local?.smsChannelEnabled || local.messageType !== 'mms') {
      return null;
    }
    const rich = local.mmsRichCard ?? pickRichCardFields(local);
    if (!this.hasMeaningfulRichCard(rich)) {
      return null;
    }
    return applyRichCardFields(
      {
        ...mapDeliveryContentsToModal(contents),
        messageType: 'mms',
        mmsRichCard: rich,
        rcsRichCard: local.rcsRichCard ?? EMPTY_RCS_RICH_CARD,
      },
      rich
    );
  }

  private getRcsTabPreviewData(): SmsEmailModalData | null {
    const contents = this.campaignData?.contents;
    const rcs = findRcsContent(contents);
    if (rcs && hasRcsDeliveryApiContent(rcs)) {
      return mapRcsApiToModal(rcs);
    }
    const local = this.deliveryData;
    if (!local?.smsChannelEnabled || local.messageType !== 'rcs') {
      return null;
    }
    const rich = local.rcsRichCard ?? pickRichCardFields(local);
    if (!this.hasMeaningfulRichCard(rich)) {
      return null;
    }
    return applyRichCardFields(
      {
        ...mapDeliveryContentsToModal(contents),
        messageType: 'rcs',
        mmsRichCard: local.mmsRichCard ?? EMPTY_MMS_RICH_CARD,
        rcsRichCard: rich,
      },
      rich
    );
  }

  private getEmailTabPreviewData(): SmsEmailModalData | null {
    const contents = this.campaignData?.contents;
    const deliveryEmail = findDeliveryEmailContent(contents);
    if (deliveryEmail && hasEmailDeliveryApiContent(deliveryEmail)) {
      return {
        ...mapDeliveryContentsToModal(contents),
        ...mapDeliveryEmailToPreviewData(deliveryEmail),
      };
    }
    const local = this.deliveryData;
    if (
      !local?.emailChannelEnabled ||
      !(local.emailSubject?.trim() || local.emailPreheader?.trim() || local.emailBody?.trim())
    ) {
      return null;
    }
    return local;
  }

  private hasMeaningfulRichCard(rich?: RichCardFormData | null): boolean {
    if (!rich) {
      return false;
    }
    return Boolean(rich.couponImageUrl?.trim() || rich.bubble2TextBody?.trim());
  }

  private stripHtmlForEmailPreview(html: string): string {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  private formatDeliveryPreviewText(text: string): string {
    return text.replace(/\{\{?\s*first_?name\s*\}?\}/gi, 'Alex');
  }

  private syncDeliveryPreviewTab(): void {
    const available = new Set(this.deliveryPreviewTabs.map(tab => tab.id));
    if (!available.size) {
      return;
    }

    const tabHasContent: Record<DeliveryPreviewTabId, boolean> = {
      sms: this.smsDeliveryPlatformEnabled && this.hasSmsTabContent,
      mms: this.smsDeliveryPlatformEnabled && this.hasMmsTabContent,
      rcs: this.smsDeliveryPlatformEnabled && this.hasRcsTabContent,
      email: this.emailDeliveryPlatformEnabled && this.hasEmailTabContent,
    };

    const current = this.selectedDeliveryPreviewTab;
    if (available.has(current) && tabHasContent[current]) {
      return;
    }

    const preference: DeliveryPreviewTabId[] = ['email', 'rcs', 'mms', 'sms'];
    for (const tab of preference) {
      if (available.has(tab) && tabHasContent[tab]) {
        this.selectedDeliveryPreviewTab = tab;
        return;
      }
    }

    this.selectedDeliveryPreviewTab = this.getDefaultDeliveryPreviewTab();
  }

  private getDefaultDeliveryPreviewTab(): DeliveryPreviewTabId {
    return this.deliveryPreviewTabs[0]?.id ?? 'sms';
  }

  get landingPagePreview(): LandingPageModalData {
    if (this.landingPageData) {
      return this.landingPageData;
    }
    const commonWebsite = findCommonWebsiteContent(this.campaignData?.contents);
    if (commonWebsite?.landing_page) {
      return mapLandingApiToModal(commonWebsite.landing_page);
    }
    return { ...DEFAULT_LANDING_PAGE_DATA };
  }

  getLandingInputPlaceholder(): string {
    const inputType = this.landingPagePreview.inputType?.toLowerCase();
    if (inputType === 'email') {
      return 'e.g. user@example.com';
    }
    return 'e.g. 555-555-5555';
  }

  getLandingConsentSegments(): ConsentTextSegment[] {
    const preview = this.landingPagePreview;
    return buildConsentTextSegments(preview.consentText, preview.consentLabel, preview.consentUrl);
  }

  get successPagePreview(): SuccessPageModalData {
    if (this.successPageData) {
      return this.successPageData;
    }
    const commonWebsite = findCommonWebsiteContent(this.campaignData?.contents);
    if (commonWebsite?.success_page) {
      return mapSuccessApiToModal(commonWebsite.success_page);
    }
    return { ...DEFAULT_SUCCESS_PAGE_DATA };
  }

  shouldShowSuccessCtaPreview(): boolean {
    return shouldShowSuccessCta(this.successPagePreview);
  }

  getSuccessPreviewLogo(): string {
    const preview = this.successPagePreview;
    if (preview.reuseLandingBrand) {
      return this.landingPagePreview.logo || '';
    }
    return preview.logoUrl || '';
  }

  getSuccessPreviewText(text: string): string {
    return replaceSuccessPageMergeFields(text, '{customer contact}', { firstName: 'Alex' });
  }

  getSuccessResendStatusText(): string {
    const preview = this.successPagePreview;
    return buildResendPreviewStatusText({
      maxAttempts: preview.maxAttempts,
      cooldownText: preview.cooldownText,
    });
  }

  private syncFromCampaignData(): void {
    const contents = this.campaignData?.contents;
    const commonWebsite = findCommonWebsiteContent(contents);

    if (commonWebsite?.landing_page) {
      this.landingPageData = mapLandingApiToModal(commonWebsite.landing_page);
    }

    if (commonWebsite?.success_page) {
      this.successPageData = mapSuccessApiToModal(commonWebsite.success_page);
    }

    if (
      hasRcsDeliveryApiContent(findRcsContent(contents)) ||
      hasMmsDeliveryApiContent(findMmsContent(contents)) ||
      hasSmsDeliveryApiContent(findSmsDeliveryContent(contents)) ||
      hasEmailDeliveryApiContent(findDeliveryEmailContent(contents))
    ) {
      this.deliveryData = mapDeliveryContentsToModal(contents);
    }

    this.syncDeliveryPreviewTab();
  }

  private applyActiveSection(section: OptionalSettingSection | ''): void {
    let target = section || 'landing';
    if (target === 'delivery' && !this.showSmsEmailDeliverySection) {
      target = 'landing';
    }
    this.landingPageExpanded = target === 'landing';
    this.successPageExpanded = target === 'success';
    this.smsEmailDeliveryExpanded = target === 'delivery';
  }

  private isCampaignPlatformSelected(platform: string): boolean {
    const platforms = this.campaignData?.platform;
    if (!Array.isArray(platforms)) {
      return false;
    }
    const normalized = platform.toLowerCase();
    return platforms
      .map((item: any) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item && typeof item === 'object') {
          const record = item as { platform?: string; id?: string };
          return record.platform || record.id || '';
        }
        return '';
      })
      .some(value => value.toLowerCase() === normalized);
  }

  /** Landing page finished → open Success Page (same step after modal save). */
  private advanceToSuccessPageSection(): void {
    this.landingPageExpanded = false;
    this.successPageExpanded = true;
    this.smsEmailDeliveryExpanded = false;
    this.landingPageCreateExpanded = false;
  }

  /** Success page finished → open SMS/Email delivery when platform allows, else finish optional settings. */
  private advanceToDeliverySection(): void {
    this.successPageCreateExpanded = false;
    if (!this.showSmsEmailDeliverySection) {
      this.completeOptionalSettingsWorkflow();
      return;
    }
    this.landingPageExpanded = false;
    this.successPageExpanded = false;
    this.smsEmailDeliveryExpanded = true;
  }

  /** Delivery finished → mark Optional Settings complete in parent. */
  private completeOptionalSettingsWorkflow(): void {
    this.landingPageExpanded = false;
    this.successPageExpanded = false;
    this.smsEmailDeliveryExpanded = false;
    this.done.emit();
  }

  private loadReuseCampaignOptions(): void {
    if (this.reuseCampaignsLoadStarted) {
      return;
    }
    this.reuseCampaignsLoadStarted = true;
    this.isLoadingPreviousLandingPages = true;
    this.isLoadingPreviousSuccessPages = true;

    this.campaignService
      .getCampaigns({ page: 1, limit: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isLoadingPreviousLandingPages = false;
          this.isLoadingPreviousSuccessPages = false;
          if (!response.status || !response.data?.campaigns) {
            return;
          }
          const campaigns = response.data.campaigns;
          this.loadedPreviousLandingPages = mapCampaignsToPreviousLandingPageOptions(
            campaigns,
            this.currentCampaignId
          );
          this.loadedPreviousSuccessPages = mapCampaignsToPreviousSuccessPageOptions(
            campaigns,
            this.currentCampaignId
          );
        },
        error: () => {
          this.isLoadingPreviousLandingPages = false;
          this.isLoadingPreviousSuccessPages = false;
          this.reuseCampaignsLoadStarted = false;
        },
      });
  }

  private reloadReuseCampaignOptions(): void {
    this.reuseCampaignsLoadStarted = false;
    this.loadedPreviousLandingPages = [];
    this.loadedPreviousSuccessPages = [];
    this.loadReuseCampaignOptions();
  }
}
